import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const fetchUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setLoading(false);
        return;
      }
      const res = await api.get('/auth/me');
      const userData = res.data.user;
      setUser(userData);
      // Sync feature flags for notification engine
      try {
        const fr = await api.get('/production/feature-flags/mine');
        localStorage.setItem('gc360_feature_flags', JSON.stringify(fr.data?.data || {}));
      } catch { /* non-fatal */ }
      // Check if must_change_password flag is set
      if (userData?.must_change_password) {
        setMustChangePassword(true);
      }
    } catch (e) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync feature flags to localStorage for notification engine
  const syncFeatureFlags = useCallback(async () => {
    try {
      const r = await api.get('/production/feature-flags/mine');
      const flags = r.data?.data || {};
      localStorage.setItem('gc360_feature_flags', JSON.stringify(flags));
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { access_token, refresh_token, user: userData, must_change_password: mcp } = res.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    setUser(userData);
    if (mcp) {
      setMustChangePassword(true);
    }
    syncFeatureFlags();
    return userData;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // Ignore
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setMustChangePassword(false);
  };

  const onPasswordChanged = async () => {
    setMustChangePassword(false);
    // Re-fetch user to get updated data
    await fetchUser();
  };

  const ADMIN_ROLES = ['Super Admin', 'Admin', 'HR', 'Finance', 'Coordinator', 'Roster Manager'];
  const STAFF_ROLES = ['Support Worker', 'Staff'];
  const isAdmin = ADMIN_ROLES.includes(user?.role_name);
  const isStaff = STAFF_ROLES.includes(user?.role_name) || (!isAdmin && !!user);
  const hasPermission = (perm) => user?.permissions?.includes(perm) || false;

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, isAdmin, isStaff, hasPermission, fetchUser,
      mustChangePassword, onPasswordChanged,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
